import type { MeResponse } from "@ondo/shared";
import { useId } from "react";

import { useDeclareCapacityController } from "./useDeclareCapacityController.ts";
import { ACCESS_REQUEST_URL, DECLARE_COPY } from "./workspace.constants.ts";

type Props = {
  me: MeResponse;
  replaceMe: (me: MeResponse) => void;
};

/**
 * The form on an empty chooser — OND-12.
 *
 * Renders what the controller decided and nothing more. The one sentence that
 * changes with the account's holdings is `view.note`, already chosen by the
 * model; the tick boxes are the eight signup descriptions, drawn the way the
 * application form draws them so the same word appears for the same value.
 *
 * The "apply for access" link survives below the form, narrowed to what it is
 * actually for: a firm's workspace is opened by the firm adding a member, and
 * no form on this account can do that.
 */
export function DeclareCapacity({ me, replaceMe }: Props) {
  const { view, toggle, submit } = useDeclareCapacityController({ me, replaceMe });
  const id = useId();

  return (
    <form
      className="workspace__declare"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <p className="workspace__note">{view.note.sentence}</p>

      <fieldset className="workspace__fieldset" aria-describedby={`${id}-hint`}>
        <legend className="workspace__legend">{DECLARE_COPY.legend}</legend>
        <p className="workspace__hint" id={`${id}-hint`}>
          {DECLARE_COPY.hint}
        </p>
        <div className="workspace__declare-choices">
          {view.options.map((option) => (
            <label
              key={option.value}
              className={`workspace__declare-choice${
                option.checked ? " workspace__declare-choice--on" : ""
              }${option.held ? " workspace__declare-choice--held" : ""}`}
            >
              <input
                className="workspace__declare-input"
                type="checkbox"
                value={option.value}
                checked={option.checked}
                disabled={option.held || view.busy}
                onChange={() => toggle(option.value)}
              />
              {option.label}
              {option.held ? <span className="workspace__declare-held"> · already yours</span> : null}
            </label>
          ))}
        </div>
      </fieldset>

      {view.error ? (
        <p className="workspace__error" role="alert">
          {view.error}
        </p>
      ) : null}

      <div className="workspace__declare-actions">
        <button className="workspace__apply" type="submit" disabled={!view.canSubmit}>
          {view.busy ? DECLARE_COPY.submitting : DECLARE_COPY.submit}
        </button>
        {!view.canSubmit && !view.busy ? (
          <span className="workspace__hint">{DECLARE_COPY.nothingChosen}</span>
        ) : null}
      </div>

      <hr className="workspace__divider" />

      <p className="workspace__note">
        {DECLARE_COPY.belongToAFirm}{" "}
        <a className="workspace__link" href={ACCESS_REQUEST_URL}>
          Apply for access <span aria-hidden="true">→</span>
        </a>
      </p>
    </form>
  );
}
